import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Users, Lightbulb, Shield, Sparkles, HandHeart, Brain, Target } from 'lucide-react';

const valueKeys = [
  { key: 'respect', icon: Heart },
  { key: 'tolerance', icon: Users },
  { key: 'responsibility', icon: Shield },
  { key: 'confidence', icon: Sparkles },
  { key: 'empathy', icon: HandHeart },
  { key: 'curiosity', icon: Lightbulb },
  { key: 'integrity', icon: Brain },
  { key: 'creativity', icon: Target },
];

export const Values = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="values" data-testid="values-section" ref={ref} className="py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('values.tag')}
          </span>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6">
            {t('values.title1')} <span className="italic">{t('values.title1Italic')}</span>
          </h2>
          <div className="section-divider mb-6" />
          <p className="font-inter text-slate-600 text-lg max-w-2xl mx-auto">
            {t('values.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
          {valueKeys.map((value, index) => (
            <motion.div
              key={value.key}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              data-testid={`value-card-${value.key}`}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 card-hover text-center group"
            >
              <div className="w-14 h-14 rounded-xl bg-slate-100 group-hover:bg-slate-900 flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                <value.icon className="w-7 h-7 text-slate-700 group-hover:text-amber-400 transition-colors duration-300" />
              </div>
              <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-2">
                {t(`values.list.${value.key}.title`)}
              </h3>
              <p className="font-inter text-slate-500 text-sm leading-relaxed">
                {t(`values.list.${value.key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-20 text-center"
        >
          <blockquote className="font-playfair text-2xl sm:text-3xl italic text-slate-700 max-w-3xl mx-auto">
            &ldquo;{t('values.quote')}&rdquo;
          </blockquote>
          <p className="font-inter text-slate-500 mt-4">{t('values.quoteAuthor')}</p>
        </motion.div>
      </div>
    </section>
  );
};
